import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  LayoutDashboard, FileQuestion, ClipboardList, BarChart3, 
  Shield, LogOut, Users, Activity, MessageSquare, Star
} from "lucide-react";

const FeedbackPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterExam, setFilterExam] = useState("all");
  const [exams, setExams] = useState([]);

  useEffect(() => { fetchData(); }, [filterExam]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [fbRes, statsRes, examsRes] = await Promise.all([
        axios.get(`${API}/admin/feedback${filterExam !== 'all' ? `?exam_id=${filterExam}` : ''}`, { headers }),
        axios.get(`${API}/admin/feedback/stats`, { headers }),
        axios.get(`${API}/exams`, { headers })
      ]);
      setFeedback(fbRes.data);
      setStats(statsRes.data);
      setExams(examsRes.data);
    } catch (error) {
      toast.error("Failed to fetch feedback");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate("/auth"); };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: FileQuestion, label: "Question Bank", path: "/admin/questions" },
    { icon: ClipboardList, label: "Exams", path: "/admin/exams" },
    { icon: BarChart3, label: "Results", path: "/admin/results" },
    { icon: Users, label: "Organisers", path: "/admin/organisers" },
    { icon: MessageSquare, label: "Feedback", path: "/admin/feedback", active: true },
    { icon: Activity, label: "IP Logs", path: "/admin/logs" }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
    ));
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="feedback-page">
      <aside className="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</span>
              <p className="text-xs text-slate-500">Examination Portal</p>
            </div>
          </div>
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div key={item.path} className={`nav-item ${item.active ? 'active' : ''}`} onClick={() => navigate(item.path)}>
                <item.icon className="w-5 h-5" /><span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><Users className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
        </div>
      </aside>

      <main className="main-content p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Candidate Feedback</h1>
              <p className="text-slate-600 mt-1">{feedback.length} responses collected</p>
            </div>
            <Select value={filterExam} onValueChange={setFilterExam}>
              <SelectTrigger className="w-60" data-testid="filter-feedback-exam"><SelectValue placeholder="Filter by exam" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Exams</SelectItem>
                {exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Stats */}
          {stats?.exam_stats?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {stats.exam_stats.map((s, i) => (
                <Card key={i} className="stats-card">
                  <div className="mb-3">
                    <p className="text-sm font-medium text-slate-900">{s._id}</p>
                    <p className="text-xs text-slate-500">{s.count} responses</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Overall</span>
                      <div className="flex items-center gap-1">{renderStars(Math.round(s.avg_rating))}<span className="text-sm font-medium ml-1">{s.avg_rating?.toFixed(1)}</span></div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Ease of Use</span>
                      <div className="flex items-center gap-1">{renderStars(Math.round(s.avg_ease))}<span className="text-sm font-medium ml-1">{s.avg_ease?.toFixed(1)}</span></div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Question Quality</span>
                      <div className="flex items-center gap-1">{renderStars(Math.round(s.avg_quality))}<span className="text-sm font-medium ml-1">{s.avg_quality?.toFixed(1)}</span></div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Feedback List */}
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : feedback.length === 0 ? (
            <Card className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-slate-900 mb-2">No Feedback Yet</h4>
              <p className="text-slate-500">Candidate feedback will appear here after they complete exams.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {feedback.map((fb, i) => (
                <Card key={fb.id} className="card-hover" data-testid={`feedback-card-${i}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-medium text-slate-900">{fb.candidate_name}</p>
                          <Badge variant="outline">{fb.exam_name}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 mb-3">{fb.candidate_email}</p>
                        <div className="flex items-center gap-6 mb-3">
                          <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Overall:</span>{renderStars(fb.rating)}</div>
                          <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Ease:</span>{renderStars(fb.ease_of_use)}</div>
                          <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Quality:</span>{renderStars(fb.question_quality)}</div>
                        </div>
                        {fb.comments && (
                          <div className="bg-slate-50 rounded-lg p-3 mt-2">
                            <p className="text-sm text-slate-700 italic">"{fb.comments}"</p>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{new Date(fb.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FeedbackPage;
